import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, Plus, User as UserIcon, LogOut, ArrowLeft, Sparkles, Shield, LayoutGrid } from "lucide-react";
import { useAuth } from "../lib/auth";

const links = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, testid: "side-overview" },
  { to: "/dashboard/services", label: "Services", icon: LayoutGrid, testid: "side-services" },
  { to: "/dashboard/orders", label: "My Orders", icon: ShoppingBag, testid: "side-orders" },
  { to: "/dashboard/new-order", label: "Quick Order", icon: Plus, testid: "side-new-order" },
  { to: "/dashboard/profile", label: "Profile", icon: UserIcon, testid: "side-profile" },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="dashboard-theme min-h-screen bg-bg" data-testid="dashboard-layout">
      <div className="grid lg:grid-cols-[256px_1fr] min-h-screen">
        <aside className="hidden lg:flex flex-col border-r border-line bg-surfaceAlt/70">
          <Link to="/" className="flex items-center gap-2 px-6 h-16 border-b border-line" data-testid="dashboard-logo">
            <div className="h-8 w-8 rounded-xl bg-ink text-white grid place-items-center">
              <Sparkles size={16} strokeWidth={2.2} />
            </div>
            <span className="font-display font-bold tracking-tight">Zenith Ranking</span>
          </Link>
          <div className="p-4 flex flex-col gap-1">
            {links.map((l) => {
              const Icon = l.icon;
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${
                      isActive ? "bg-white shadow-[0_4px_14px_rgba(0,0,0,0.05)] text-ink font-semibold" : "text-inkSoft hover:bg-white/60 hover:text-ink"
                    }`
                  }
                  data-testid={l.testid}
                >
                  <Icon size={16} />
                  {l.label}
                </NavLink>
              );
            })}
            {user?.is_admin && (
              <Link
                to="/admin"
                className="mt-4 flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm bg-ink text-white hover:bg-black transition"
                data-testid="side-admin-console"
              >
                <Shield size={16} /> Admin console
              </Link>
            )}
          </div>
          <div className="mt-auto p-4 border-t border-line">
            <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-inkSoft hover:text-ink">
              <ArrowLeft size={14} /> Back to site
            </Link>
            <div className="mt-3 px-3 py-3 rounded-xl bg-white border border-line">
              <div className="flex items-center gap-3">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-ink text-white grid place-items-center text-sm font-semibold">
                    {(user?.name || "U").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-xs text-inkSoft truncate">{user?.email}</div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs text-inkSoft hover:text-terra py-2 rounded-lg border border-line"
                data-testid="dashboard-logout"
              >
                <LogOut size={12} /> Log out
              </button>
            </div>
          </div>
        </aside>

        <div className="flex flex-col">
          <header className="lg:hidden h-14 px-4 border-b border-line flex items-center justify-between bg-white">
            <Link to="/dashboard" className="flex items-center gap-2 font-display font-bold">
              <Sparkles size={16} /> Zenith Ranking
            </Link>
            <button onClick={handleLogout} className="text-xs text-inkSoft" data-testid="mobile-logout">Log out</button>
          </header>
          <div className="p-6 lg:p-10 max-w-6xl w-full">{children}</div>
        </div>
      </div>
    </div>
  );
}
